/* eslint-disable no-console */
/* eslint-disable max-len */
/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState, useReducer } from "react";
import toast from "react-hot-toast";
import { FaEdit, FaTrash } from "react-icons/fa";
import { FiSave } from "react-icons/fi";
import { Table } from "reactstrap";
import { Loader } from "..";
import { useAuthContext } from "../../context/auth";
import {
  getCategorias,
  getIntermediarios,
  getClientes,
  getSubcategorias,
  getUsers,
  updateCliente,
  deleteCliente,
} from "../../utils";
import { fetchTodosLosContactosPorLado, sincronizarContactosCliente } from "../../utils/contactosCliente";
import styles from "./stylesClientesListado.module.scss";

// Al abrir el select muestra info extra (agencia/email), al cerrar solo nombre
const handleContactSelectFocus = (e) => {
  [...e.target.options].forEach((opt) => {
    if (opt.value && opt.dataset.full) opt.textContent = opt.dataset.full;
  });
};
const handleContactSelectBlur = (e) => {
  [...e.target.options].forEach((opt) => {
    if (opt.value && opt.dataset.short) opt.textContent = opt.dataset.short;
  });
};

// Separate component for list items
const ContactListItem = ({ index, value, onChange, users, role, excludeIds, paisMap, pais, intermediarios }) => {
  const [, forceItemUpdate] = useReducer((x) => x + 1, 0);

  const sortedUsers = useMemo(
    () =>
      users
        .filter(
          (user) =>
            user.rol?.trim().toLowerCase() === role &&
            user.pais === (paisMap[pais] || pais) &&
            !excludeIds.includes(String(user.id))
        )
        .sort((a, b) => {
          const nombreComparison = a.nombre.localeCompare(b.nombre);
          return nombreComparison !== 0 ? nombreComparison : a.apellido.localeCompare(b.apellido);
        }),
    [users, role, excludeIds, paisMap, pais]
  );

  const getFullLabel = (user) => {
    const base = `${user.nombre} ${user.apellido}`;
    if (role === "agencia" && intermediarios) {
      const inter = intermediarios.find((i) => i.id === user.agencia);
      const name = inter ? (inter.nombre.includes("|") ? inter.nombre.split("|")[1].trim() : inter.nombre) : "";
      return name ? `${base}, ${name}` : base;
    }
    if (role === "cliente" && user.email) return `${base}, ${user.email}`;
    return base;
  };

  return (
    <div key={`contact-${index}`} className={styles.contactoClienteItem}>
      <select
        key={`${value || "default"}-${index}-${Date.now()}`}
        className={`${styles.smallInput} mb-1 ${styles.contactoClienteDropdown}`}
        value={value || ""}
        onChange={(e) => {
          const selectedValue = e.target.value;
          onChange(selectedValue === "" ? null : selectedValue);
          forceItemUpdate();
        }}
        onFocus={handleContactSelectFocus}
        onBlur={handleContactSelectBlur}
      >
        <option value="">Desasociar</option>
        {sortedUsers.map((user) => {
          const shortLabel = `${user.nombre} ${user.apellido}`;
          return (
            <option key={user.id} value={user.id} data-short={shortLabel} data-full={getFullLabel(user)}>
              {shortLabel}
            </option>
          );
        })}
      </select>
    </div>
  );
};

export const ClientesListado = ({ renderHeader }) => {
  const [clientes, setClientes] = useState();
  const [categorias, setCategorias] = useState();
  const [intermediarios, setIntermediarios] = useState();
  const [usuarios, setUsuarios] = useState();
  const [editingClienteId, setEditingClienteId] = useState(null);
  // Proyecto Contactos, Fase 3: una lista por lado, sin "contacto principal" separado (antes
  // contacto_cliente/agencia + una lista de hasta 3 más — ver docs/modulos/clientes.md). Orden por
  // `id` de vínculo (mismo que entrega `contactosPorLado`, lector único de Fase 2). `editContactos*`
  // es lo que el usuario está editando; `editContactos*Antes` es la foto de lo que la pantalla
  // tenía cargado AL ENTRAR en edición — el `antes` que necesita `sincronizarContactosCliente` y la
  // base de comparación de `hasChanges` (nunca recalculado contra el estado en vivo, que podría
  // haber cambiado por otra fila).
  const [editContactosCliente, setEditContactosCliente] = useState([]);
  const [editContactosClienteAntes, setEditContactosClienteAntes] = useState([]);
  const [editContactosAgencia, setEditContactosAgencia] = useState([]);
  const [editContactosAgenciaAntes, setEditContactosAgenciaAntes] = useState([]);
  // `Map<cliente_id, usuario_id[]>` por lado — fuente única de lectura de contactos (10.229, mismo
  // lector que el CRM de Fase 2), reemplaza los 8 casilleros escalares en vista Y en edición.
  const [contactosPorLado, setContactosPorLado] = useState({ cliente: new Map(), agencia: new Map() });
  const [editCategoriaCliente, setEditCategoriaCliente] = useState(null);
  const [editIntermediario, setEditIntermediario] = useState(null);
  const [editCategoria, setEditCategoria] = useState(null);
  const [editSubcategoria, setEditSubcategoria] = useState(null);
  const [subcategorias, setSubcategorias] = useState([]);
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroProducto, setFiltroProducto] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroSubcategoria, setFiltroSubcategoria] = useState("");
  const [filtroIntermediario, setFiltroIntermediario] = useState("");
  const [filtroEjecutivo, setFiltroEjecutivo] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [deleteClienteId, setDeleteClienteId] = useState(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [editEjecutivosAsignados, setEditEjecutivosAsignados] = useState([]);
  const [dropdownCountEjecutivos, setDropdownCountEjecutivos] = useState(1);

  const { pais, rol, id } = useAuthContext();

  const categoriaMap = useMemo(() => {
    const map = {};
    if (categorias) {
      categorias.forEach(({ id, nombre }) => {
        map[id] = nombre;
      });
    }
    return map;
  }, [categorias]);

  const intermediarioMap = useMemo(() => {
    const map = {};
    if (intermediarios) {
      intermediarios.forEach(({ id, nombre }) => {
        map[id] = nombre;
      });
    }
    return map;
  }, [intermediarios]);

  const contactoMap = useMemo(() => {
    const map = {};
    if (usuarios) {
      usuarios.forEach(({ id, nombre, apellido }) => {
        map[id] = `${nombre} ${apellido}`.trim();
      });
    }
    return map;
  }, [usuarios]);

  const subcategoriaMap = useMemo(() => {
    const map = {};
    if (subcategorias) {
      subcategorias.forEach(({ id, nombre }) => {
        map[id] = nombre;
      });
    }
    return map;
  }, [subcategorias]);

  const paisMap = {
    Perú: "PER",
    Chile: "CHI",
    Uruguay: "URU",
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const [newClientes, newIntermediarios, newCategorias, newUsuarios, newSubcategorias, contactosPorLadoData] =
          await Promise.all([
            getClientes({ pais }),
            getIntermediarios({ pais }),
            getCategorias(),
            getUsers(),
            getSubcategorias(),
            fetchTodosLosContactosPorLado().catch(() => ({ cliente: new Map(), agencia: new Map() })),
          ]);

        setClientes(newClientes || []);
        setContactosPorLado(contactosPorLadoData);
        setIntermediarios(newIntermediarios);
        setCategorias(newCategorias);
        setUsuarios(newUsuarios);
        setSubcategorias(newSubcategorias);
      } catch {
        toast.error("Error al cargar los datos");
      }
    };

    initialize();
  }, [pais]);

  const canEdit = (cliente) =>
    rol === "superadmin" ||
    rol === "gerencia" ||
    (pais === "Chile" && cliente.ejecutivos_asignados?.includes(Number(id))) ||
    (pais === "Perú" && rol === "comercial") ||
    (rol === "comercial" && String(id) === String(cliente.ejecutivo));

  const handleUpdateCliente = async (clienteId, originalCliente) => {
    try {
      const updatedData = {
        id: clienteId,
        ultimo_contacto_fecha: originalCliente.ultimo_contacto_fecha,
        ultimo_contacto_accion: originalCliente.ultimo_contacto_accion,
        proximo_contacto_fecha: originalCliente.proximo_contacto_fecha,
        proximo_contacto_accion: originalCliente.proximo_contacto_accion,
        contactoId: originalCliente.contactoId,
        // Proyecto Contactos, Fase 3: `updateCliente` ya NO escribe las 8 columnas legacy
        // (contacto_cliente/_1/_2/_3, contacto_agencia/_1/_2/_3) — las llena el trigger
        // `sync_contactos_*_to_legacy` (migración 20260926f, APLICADA) a partir de la relacional,
        // que es la que `sincronizarContactosCliente` escribe más abajo.
        categoria_cliente:
          editCategoriaCliente !== null && editCategoriaCliente !== undefined
            ? editCategoriaCliente || null
            : originalCliente.categoria_cliente,
        agencia:
          editIntermediario !== null && editIntermediario !== undefined
            ? editIntermediario
              ? Number(editIntermediario)
              : null
            : originalCliente.agencia,
        categoria:
          editCategoria !== null && editCategoria !== undefined
            ? editCategoria
              ? Number(editCategoria)
              : null
            : originalCliente.categoria,
        subcategoria_id:
          editSubcategoria !== null && editSubcategoria !== undefined
            ? editSubcategoria
              ? Number(editSubcategoria)
              : null
            : originalCliente.subcategoria_id,
        ejecutivos_asignados: editEjecutivosAsignados.filter((id) => id).map(Number), // Save as array of numbers, filtering empty
      };

      // Categoría obligatoria (decisión de Ignacio, 2026-10-07: ningún cliente sin categoría) —
      // bloquea el guardado completo si el resultado final quedaría sin categoría, sin importar
      // si fue ESTE edit el que la vació o si el cliente ya venía sin ella desde antes (10.177).
      if (!updatedData.categoria) {
        toast.error("La categoría es obligatoria — elegí una antes de guardar");
        return;
      }

      // Auto-detectar agencia si hay contactos agencia pero intermediario es DIRECTO o null —
      // Fase 3: el primer id de la lista de agencia (orden por id de vínculo), ya no el escalar.
      const contactosAgenciaIds = editContactosAgencia.filter(Boolean).map(Number);
      const tieneContactosAgencia = contactosAgenciaIds.length > 0;

      if (tieneContactosAgencia) {
        const agenciaActual = updatedData.agencia;
        const nombreAgencia = agenciaActual ? (intermediarioMap[agenciaActual] || "").toUpperCase() : "";
        const esDirectoONull = !agenciaActual || nombreAgencia === "DIRECTO";

        if (esDirectoONull) {
          const primerContactoId = contactosAgenciaIds[0];
          const usuarioContacto = usuarios.find((u) => u.id === primerContactoId);
          if (usuarioContacto?.agencia && intermediarioMap[usuarioContacto.agencia]) {
            updatedData.agencia = usuarioContacto.agencia;
            toast.success(
              `Intermediario actualizado a "${intermediarioMap[usuarioContacto.agencia]}" (detectado del contacto asignado)`
            );
          }
        }
      }

      // Fase 3: hasChanges compara LISTAS (orden por id de vínculo), no casilleros.
      const contactosClienteIds = editContactosCliente.filter(Boolean).map(Number);
      const hasChanges =
        !arraysEqual(contactosClienteIds, editContactosClienteAntes.filter(Boolean).map(Number)) ||
        !arraysEqual(contactosAgenciaIds, editContactosAgenciaAntes.filter(Boolean).map(Number)) ||
        editCategoriaCliente !== originalCliente.categoria_cliente ||
        editIntermediario !== originalCliente.agencia ||
        (editCategoria !== null &&
          editCategoria !== undefined &&
          String(editCategoria) !== String(originalCliente.categoria)) ||
        !arraysEqual(
          editEjecutivosAsignados.filter((id) => id),
          originalCliente.ejecutivos_asignados || []
        ); // Check for executives changes

      if (hasChanges) {
        await updateCliente(updatedData);
        // Sincronizar las dos tablas relacionales — escritor único (10.229): `antes` es la foto
        // tomada al entrar en edición (`editContactos*Antes`), `despues` es la lista actual — sin
        // tope de 4, nunca toca un id que ninguna de las dos listas menciona (un 5º+ contacto de
        // otra sesión, u OMNET en agencia, quedan siempre intactos).
        try {
          await sincronizarContactosCliente({
            clienteId,
            lado: "agencia",
            antes: editContactosAgenciaAntes,
            despues: contactosAgenciaIds,
          });
        } catch (e) {
          console.error("Error sincronizando contactos agencia relacional:", e);
        }
        try {
          await sincronizarContactosCliente({
            clienteId,
            lado: "cliente",
            antes: editContactosClienteAntes,
            despues: contactosClienteIds,
          });
        } catch (e) {
          console.error("Error sincronizando contactos cliente relacional:", e);
        }
        setContactosPorLado((prev) => ({
          cliente: new Map(prev.cliente).set(clienteId, contactosClienteIds),
          agencia: new Map(prev.agencia).set(clienteId, contactosAgenciaIds),
        }));
        setClientes(
          clientes.map((cliente) =>
            cliente.id === clienteId
              ? {
                  ...cliente,
                  categoria_cliente: updatedData.categoria_cliente,
                  categoria: updatedData.categoria,
                  subcategoria_id: updatedData.subcategoria_id,
                  agencia: updatedData.agencia,
                  ejecutivo: updatedData.ejecutivo,
                  ejecutivos_asignados: updatedData.ejecutivos_asignados,
                }
              : cliente
          )
        );
        toast.success("Cliente actualizado correctamente");
      } else {
        toast("No se realizaron cambios");
      }
      setEditingClienteId(null);
      setEditContactosCliente([]);
      setEditContactosClienteAntes([]);
      setEditContactosAgencia([]);
      setEditContactosAgenciaAntes([]);
      setEditCategoriaCliente(null);
      setEditIntermediario(null);
      setEditCategoria(null);
      setEditSubcategoria(null);
      setEditEjecutivosAsignados([]);
      setDropdownCountEjecutivos(1);
    } catch (error) {
      console.error("Error al actualizar cliente:", error.message);
      if (error.message.includes("network") || error.message.includes("timeout")) {
        toast.error("Error de conexión. Intenta de nuevo.");
      } else {
        toast.error("Error al actualizar el cliente. Contacta al soporte.");
      }
    }
  };

  const confirmDeleteCliente = async () => {
    if (!deleteClienteId) return;
    try {
      await deleteCliente(deleteClienteId);
      setClientes(clientes.filter((c) => c.id !== deleteClienteId));
      toast.success("Cliente eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar cliente:", error);
      toast.error("Error al eliminar el cliente");
    } finally {
      setDeleteClienteId(null);
    }
  };

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => Number(val) === b[index]);
  };

  // Cascada inversa producto→empresa. Este componente usa IDs (cliente.id) en los filtros,
  // no strings de empresa/producto como VentasDetalle, por eso NO usa useEmpresaProductoFilter.
  // TODO: unificar con useEmpresaProductoFilter migrando estos filtros a strings.
  const empresaNombreDeProducto = useMemo(
    () => (filtroProducto ? clientes?.find((c) => String(c.id) === String(filtroProducto))?.empresa || "" : ""),
    [filtroProducto, clientes]
  );
  const empresaRepIdDeProducto = useMemo(
    () =>
      empresaNombreDeProducto ? String(clientes?.find((c) => c.empresa === empresaNombreDeProducto)?.id ?? "") : "",
    [empresaNombreDeProducto, clientes]
  );
  const empresaDisabled = Boolean(filtroProducto) && Boolean(empresaRepIdDeProducto);

  // Auto-completar la empresa cuando el producto la determina unívocamente (guard anti-loop).
  useEffect(() => {
    if (empresaDisabled && String(filtroEmpresa) !== empresaRepIdDeProducto) {
      setFiltroEmpresa(empresaRepIdDeProducto);
    }
  }, [empresaDisabled, empresaRepIdDeProducto, filtroEmpresa]);

  const renderEmpresaOptions = () => {
    const empresasMap = new Map();
    clientes?.forEach((cliente) => {
      if (cliente.empresa && !empresasMap.has(cliente.empresa)) {
        empresasMap.set(cliente.empresa, cliente);
      }
    });

    const empresasOrdenadas = [...empresasMap.values()].sort((a, b) => a.empresa.localeCompare(b.empresa));
    // Cascada inversa: si hay producto seleccionado, mostrar solo su empresa.
    const empresas = empresaNombreDeProducto
      ? empresasOrdenadas.filter((cliente) => cliente.empresa === empresaNombreDeProducto)
      : empresasOrdenadas;

    return empresas.map((cliente) => (
      <option key={cliente.id} value={cliente.id}>
        {cliente.empresa}
      </option>
    ));
  };

  const renderProductoOptions = () => {
    const clientesFiltradosPorEmpresa = filtroEmpresa
      ? clientes?.filter((cliente) => {
        const empresaCliente = clientes?.find((c) => c.id === Number(filtroEmpresa))?.empresa;
        return cliente.empresa === empresaCliente;
      })
      : clientes;

    const productosMap = new Map();
    clientesFiltradosPorEmpresa?.forEach((cliente) => {
      if (cliente.producto && !productosMap.has(cliente.producto)) {
        productosMap.set(cliente.producto, cliente);
      }
    });

    const productos = [...productosMap.values()].sort((a, b) => a.producto.localeCompare(b.producto));

    return productos.map((cliente) => (
      <option key={cliente.id} value={cliente.id}>
        {cliente.producto}
      </option>
    ));
  };

  const renderTipoOptions = () => {
    const tipos = ["A", "B", "C"];
    return tipos.map((tipo) => (
      <option key={tipo} value={tipo}>
        {tipo}
      </option>
    ));
  };

  const renderCategoriaOptions = () =>
    categorias
      ?.sort((a, b) => a.nombre.localeCompare(b.nombre))
      .map((categoria) => (
        <option key={categoria.id} value={categoria.id}>
          {categoria.nombre}
        </option>
      ));

  const renderIntermediariosOptions = () =>
    intermediarios
      ?.sort((a, b) => a.nombre.localeCompare(b.nombre))
      .map((intermediario) => (
        <option key={intermediario.id} value={intermediario.id}>
          {intermediario.nombre}
        </option>
      ));

  const renderEjecutivosOptions = () => {
    const paisCode = paisMap[pais] || pais;

    return usuarios
      ?.filter((user) => ["gerencia", "comercial"].includes(user.rol) && user.pais === paisCode)
      .sort((a, b) => {
        const nombreComparison = a.nombre.localeCompare(b.nombre);
        return nombreComparison !== 0 ? nombreComparison : a.apellido.localeCompare(b.apellido);
      })
      .map((user) => (
        <option key={user.id} value={user.id}>
          {`${user.nombre} ${user.apellido}`}
        </option>
      ));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const clientesFiltrados = useMemo(() => {
    const filtered = clientes?.filter((cliente) => {
      const empresaSeleccionada = filtroEmpresa ? clientes?.find((c) => c.id === Number(filtroEmpresa))?.empresa : null;
      const empresaMatch = !filtroEmpresa || cliente.empresa === empresaSeleccionada;
      const productoMatch = !filtroProducto || String(cliente.id) === String(filtroProducto);
      const tipoMatch = !filtroTipo || cliente.categoria_cliente === filtroTipo;
      const categoriaMatch = !filtroCategoria || String(cliente.categoria) === String(filtroCategoria);
      const subcategoriaMatch = !filtroSubcategoria || String(cliente.subcategoria_id) === String(filtroSubcategoria);
      const intermediarioMatch = !filtroIntermediario || String(cliente.agencia) === String(filtroIntermediario);
      const ejecutivoMatch =
        !filtroEjecutivo ||
        (cliente.ejecutivos_asignados && cliente.ejecutivos_asignados.includes(Number(filtroEjecutivo)));

      return (
        empresaMatch &&
        productoMatch &&
        tipoMatch &&
        categoriaMatch &&
        subcategoriaMatch &&
        intermediarioMatch &&
        ejecutivoMatch
      );
    });

    if (!sortColumn || !filtered) return filtered;

    return [...filtered].sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case "empresa":
          valA = (a.empresa || "").toLowerCase();
          valB = (b.empresa || "").toLowerCase();
          break;
        case "producto":
          valA = (a.producto || "").toLowerCase();
          valB = (b.producto || "").toLowerCase();
          break;
        case "tipo":
          valA = a.categoria_cliente || "";
          valB = b.categoria_cliente || "";
          break;
        case "categoria":
          valA = (categoriaMap[a.categoria] || "").toLowerCase();
          valB = (categoriaMap[b.categoria] || "").toLowerCase();
          break;
        case "subcategoria":
          valA = (subcategoriaMap[a.subcategoria_id] || "").toLowerCase();
          valB = (subcategoriaMap[b.subcategoria_id] || "").toLowerCase();
          break;
        case "intermediario":
          valA = (intermediarioMap[a.agencia] || "").toLowerCase();
          valB = (intermediarioMap[b.agencia] || "").toLowerCase();
          break;
        case "ejecutivo":
          valA = (a.ejecutivos_asignados?.[0] ? contactoMap[a.ejecutivos_asignados[0]] || "" : "").toLowerCase();
          valB = (b.ejecutivos_asignados?.[0] ? contactoMap[b.ejecutivos_asignados[0]] || "" : "").toLowerCase();
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    clientes,
    filtroEmpresa,
    filtroProducto,
    filtroTipo,
    filtroCategoria,
    filtroSubcategoria,
    filtroIntermediario,
    filtroEjecutivo,
    sortColumn,
    sortDirection,
    categoriaMap,
    subcategoriaMap,
    intermediarioMap,
    contactoMap,
  ]);

  if (intermediarios === undefined || categorias === undefined || clientes === undefined || usuarios === undefined) {
    return <Loader />;
  }

  const filtersContent = (
    <div className={styles.filtersContainer}>
      <div className={styles.filterRow}>
        <select
          className={`form-select ${filtroEmpresa ? styles.activeFilter : ""}`}
          value={filtroEmpresa}
          onChange={(e) => {
            setFiltroEmpresa(e.target.value);
            setFiltroProducto("");
          }}
          disabled={empresaDisabled}
          style={empresaDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
        >
          <option value="">{empresaDisabled ? empresaNombreDeProducto : "Empresa/Marca"}</option>
          {renderEmpresaOptions()}
        </select>
        <select
          className={`form-select ${filtroProducto ? styles.activeFilter : ""}`}
          value={filtroProducto}
          onChange={(e) => {
            setFiltroProducto(e.target.value);
            setFiltroEmpresa("");
          }}
        >
          <option value="">Producto</option>
          {renderProductoOptions()}
        </select>
        <select
          className={`form-select ${filtroTipo ? styles.activeFilter : ""}`}
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
        >
          <option value="">Tipo</option>
          {renderTipoOptions()}
        </select>
        <select
          className={`form-select ${filtroCategoria ? styles.activeFilter : ""}`}
          value={filtroCategoria}
          onChange={(e) => {
            setFiltroCategoria(e.target.value);
            setFiltroSubcategoria("");
          }}
        >
          <option value="">Categoría</option>
          {renderCategoriaOptions()}
        </select>
        <select
          className={`form-select ${filtroSubcategoria ? styles.activeFilter : ""}`}
          value={filtroSubcategoria}
          onChange={(e) => setFiltroSubcategoria(e.target.value)}
          disabled={!filtroCategoria || !subcategorias?.some((s) => String(s.categoria_id) === String(filtroCategoria))}
        >
          <option value="">Subcategoría</option>
          {filtroCategoria &&
            subcategorias
              ?.filter((s) => String(s.categoria_id) === String(filtroCategoria))
              .sort((a, b) => a.orden - b.orden)
              .map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.nombre}
                </option>
              ))}
        </select>
        <select
          className={`form-select ${filtroIntermediario ? styles.activeFilter : ""}`}
          value={filtroIntermediario}
          onChange={(e) => setFiltroIntermediario(e.target.value)}
        >
          <option value="">Intermediario</option>
          {renderIntermediariosOptions()}
        </select>
        <select
          className={`form-select ${filtroEjecutivo ? styles.activeFilter : ""}`}
          value={filtroEjecutivo}
          onChange={(e) => setFiltroEjecutivo(e.target.value)}
        >
          <option value="">Ejecutivo</option>
          {renderEjecutivosOptions()}
        </select>
      </div>
    </div>
  );

  return (
    <>
      {renderHeader ? renderHeader(filtersContent) : filtersContent}
      <div className={styles.clientesListado}>
        {/* Tabla */}
        <Table responsive striped>
          <thead className="table-dark">
            <tr>
              {[
                { key: "empresa", label: "Empresa/Marca" },
                { key: "producto", label: "Producto" },
                { key: "tipo", label: "Tipo" },
                { key: "categoria", label: "Categoría" },
                { key: "subcategoria", label: "Subcategoría" },
                { key: "intermediario", label: "Intermediario" },
                { key: "ejecutivo", label: "Ejecutivo/s" },
                { key: null, label: "Contacto Cliente" },
                { key: null, label: "Contacto Agencia" },
                { key: null, label: "Edit" },
              ].map((col, i) => (
                <th
                  key={i}
                  onClick={col.key ? () => handleSort(col.key) : undefined}
                  style={col.key ? { cursor: "pointer", userSelect: "none" } : undefined}
                >
                  {col.label}
                  {col.key && sortColumn === col.key && (
                    <span style={{ marginLeft: 4 }}>{sortDirection === "asc" ? " ▲" : " ▼"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clientesFiltrados.map((cliente) => (
              <tr key={cliente.id}>
                <td>{cliente.empresa || "-"}</td>
                <td>{cliente.producto || "-"}</td>
                <td>
                  {editingClienteId === cliente.id && (String(id) === "1" || String(id) === "4") ? (
                    <select
                      className={styles.smallInput}
                      value={editCategoriaCliente || ""}
                      onChange={(e) => setEditCategoriaCliente(e.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {["A", "B", "C"].map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {tipo}
                        </option>
                      ))}
                    </select>
                  ) : (
                    cliente.categoria_cliente || "-"
                  )}
                </td>
                <td>
                  {editingClienteId === cliente.id && (String(id) === "1" || String(id) === "4") ? (
                    <select
                      className={styles.smallInput}
                      value={editCategoria || ""}
                      onChange={(e) => setEditCategoria(e.target.value)}
                      style={!editCategoria ? { border: "1px solid #dc3545" } : undefined}
                      title={!editCategoria ? "Obligatoria — no se puede guardar sin categoría" : undefined}
                    >
                      <option value="">Seleccionar</option>
                      {categorias
                        ?.sort((a, b) => a.nombre.localeCompare(b.nombre))
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nombre}
                          </option>
                        ))}
                    </select>
                  ) : (
                    categoriaMap[cliente.categoria] || "-"
                  )}
                </td>
                <td>
                  {editingClienteId === cliente.id && (String(id) === "1" || String(id) === "4") ? (
                    <select
                      className={styles.smallInput}
                      value={editSubcategoria || ""}
                      onChange={(e) => setEditSubcategoria(e.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {subcategorias
                        ?.filter((s) => {
                          const catId = editCategoria || cliente.categoria;
                          return String(s.categoria_id) === String(catId);
                        })
                        .sort((a, b) => a.orden - b.orden)
                        .map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.nombre}
                          </option>
                        ))}
                    </select>
                  ) : (
                    subcategoriaMap[cliente.subcategoria_id] || "-"
                  )}
                </td>
                <td>
                  {editingClienteId === cliente.id && canEdit(cliente) ? (
                    <select
                      className={styles.smallInput}
                      value={editIntermediario || ""}
                      onChange={(e) => setEditIntermediario(e.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {intermediarios
                        ?.sort((a, b) => a.nombre.localeCompare(b.nombre))
                        .map((intermediario) => (
                          <option key={intermediario.id} value={intermediario.id}>
                            {intermediario.nombre}
                          </option>
                        ))}
                    </select>
                  ) : (
                    intermediarioMap[cliente.agencia] || "-"
                  )}
                </td>
                <td>
                  {editingClienteId === cliente.id && rol === "gerencia" ? (
                    <div className={styles.contactoWrapper}>
                      <div className={styles.contactoEjecutivosContainer}>
                        {Array.from({ length: dropdownCountEjecutivos }).map((_, index) => (
                          <div key={`executive-${index}`} className={styles.contactoEjecutivosItem}>
                            <select
                              className={`${styles.smallInput} mb-1 ${styles.contactoEjecutivosDropdown}`}
                              value={editEjecutivosAsignados[index] || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const newList = [...editEjecutivosAsignados];
                                newList[index] = newValue === "" ? null : newValue;
                                setEditEjecutivosAsignados(newList);
                                forceUpdate(); // Force re-render
                              }}
                            >
                              <option value="">Desasociar</option>
                              {usuarios
                                .filter(
                                  (user) =>
                                    ["comercial", "gerencia"].includes(user.rol?.trim().toLowerCase()) &&
                                    user.pais === (paisMap[pais] || pais) &&
                                    user.habilitado !== false &&
                                    !editEjecutivosAsignados
                                      .filter((id, i) => i !== index && id)
                                      .includes(String(user.id))
                                )
                                .sort((a, b) => {
                                  const nombreComparison = a.nombre.localeCompare(b.nombre);
                                  return nombreComparison !== 0
                                    ? nombreComparison
                                    : a.apellido.localeCompare(b.apellido);
                                })
                                .map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {`${user.nombre} ${user.apellido}`}
                                  </option>
                                ))}
                            </select>
                          </div>
                        ))}
                        {dropdownCountEjecutivos < 4 && (
                          <div className={styles.contactoEjecutivosItem}>
                            <button
                              onClick={() => {
                                setDropdownCountEjecutivos(dropdownCountEjecutivos + 1);
                                setEditEjecutivosAsignados([...editEjecutivosAsignados, ""]);
                              }}
                              className={`btn btn-sm btn-outline-primary ${styles.addContactBtn}`}
                              disabled={dropdownCountEjecutivos >= 4}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <ul className={`list-unstyled ${styles.contactoEjecutivoList}`}>
                      {cliente.ejecutivos_asignados?.length > 0 ? (
                        cliente.ejecutivos_asignados.map((id, index) => <li key={index}>{contactoMap[id] || "-"}</li>)
                      ) : (
                        <li>-</li>
                      )}
                    </ul>
                  )}
                </td>
                <td>
                  {editingClienteId === cliente.id && canEdit(cliente) ? (
                    <div className={styles.contactoWrapper}>
                      <div className={styles.contactoClienteContainer}>
                        {editContactosCliente.map((value, index) => (
                          <div
                            key={`client-${index}`}
                            className={styles.contactoClienteItem}
                            style={{ display: "flex", alignItems: "center", gap: 4 }}
                          >
                            <ContactListItem
                              index={index}
                              value={value}
                              onChange={(newValue) => {
                                const newList = [...editContactosCliente];
                                newList[index] = newValue;
                                setEditContactosCliente(newList);
                                forceUpdate();
                              }}
                              users={usuarios}
                              role="cliente"
                              excludeIds={editContactosCliente.filter((_, i) => i !== index).filter(Boolean)}
                              paisMap={paisMap}
                              pais={pais}
                              intermediarios={intermediarios}
                            />
                            <FaTrash
                              onClick={() => {
                                setEditContactosCliente(editContactosCliente.filter((_, i) => i !== index));
                                forceUpdate();
                              }}
                              style={{ cursor: "pointer", color: "#dc3545", fontSize: ".8rem" }}
                              title="Quitar"
                            />
                          </div>
                        ))}
                        <div className={styles.contactoClienteItem}>
                          <button
                            type="button"
                            onClick={() => setEditContactosCliente([...editContactosCliente, ""])}
                            className={`btn btn-sm btn-outline-primary ${styles.addContactBtn}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <ul className={`list-unstyled ${styles.contactoClienteList}`}>
                      {(contactosPorLado.cliente.get(cliente.id) || []).map((uid, index) => (
                        <li key={index}>{contactoMap[uid] || "-"}</li>
                      ))}
                      {(contactosPorLado.cliente.get(cliente.id) || []).length === 0 && <li>-</li>}
                    </ul>
                  )}
                </td>
                <td>
                  {editingClienteId === cliente.id && canEdit(cliente) ? (
                    <div className={styles.contactoWrapper}>
                      <div className={styles.contactoAgenciaContainer}>
                        {editContactosAgencia.map((value, index) => (
                          <div
                            key={`agency-${index}`}
                            className={styles.contactoAgenciaItem}
                            style={{ display: "flex", alignItems: "center", gap: 4 }}
                          >
                            <ContactListItem
                              index={index}
                              value={value}
                              onChange={(newValue) => {
                                const newList = [...editContactosAgencia];
                                newList[index] = newValue;
                                setEditContactosAgencia(newList);
                                forceUpdate();
                              }}
                              users={usuarios}
                              role="agencia"
                              excludeIds={editContactosAgencia.filter((_, i) => i !== index).filter(Boolean)}
                              paisMap={paisMap}
                              pais={pais}
                              intermediarios={intermediarios}
                            />
                            <FaTrash
                              onClick={() => {
                                setEditContactosAgencia(editContactosAgencia.filter((_, i) => i !== index));
                                forceUpdate();
                              }}
                              style={{ cursor: "pointer", color: "#dc3545", fontSize: ".8rem" }}
                              title="Quitar"
                            />
                          </div>
                        ))}
                        <div className={styles.contactoAgenciaItem}>
                          <button
                            type="button"
                            onClick={() => setEditContactosAgencia([...editContactosAgencia, ""])}
                            className={`btn btn-sm btn-outline-primary ${styles.addContactBtn}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <ul className={`list-unstyled ${styles.contactoAgenciaList}`}>
                      {(contactosPorLado.agencia.get(cliente.id) || []).map((uid, index) => {
                        const user = usuarios.find((u) => u.id === uid);
                        return (
                          <li key={index}>
                            {contactoMap[uid] || "-"}
                            {user?.cargo && (
                              <span style={{ fontSize: ".7rem", color: "#888", marginLeft: 4 }}>{user.cargo}</span>
                            )}
                          </li>
                        );
                      })}
                      {(contactosPorLado.agencia.get(cliente.id) || []).length === 0 && <li>-</li>}
                    </ul>
                  )}
                </td>
                <td className="text-center">
                  {canEdit(cliente) ? (
                    editingClienteId === cliente.id ? (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <FiSave
                          onClick={() => handleUpdateCliente(cliente.id, cliente)}
                          style={{ cursor: "pointer" }}
                        />
                        {(String(id) === "1" || String(id) === "4") && (
                          <FaTrash
                            onClick={() => setDeleteClienteId(cliente.id)}
                            style={{ cursor: "pointer", color: "#000" }}
                          />
                        )}
                      </span>
                    ) : (
                      <FaEdit
                        onClick={() => {
                          setEditingClienteId(cliente.id);
                          // Proyecto Contactos, Fase 3: la lista de edición sale de la relacional
                          // (`contactosPorLado`, mismo orden por id de vínculo que el lector único
                          // de Fase 2) — nunca de los 8 casilleros escalares. `*Antes` congela la
                          // foto de entrada, es el `antes` que usa `sincronizarContactosCliente` y
                          // la base de `hasChanges` al guardar.
                          const contactosClienteIniciales = (contactosPorLado.cliente.get(cliente.id) || []).map(
                            String
                          );
                          const contactosAgenciaIniciales = (contactosPorLado.agencia.get(cliente.id) || []).map(
                            String
                          );
                          setEditContactosCliente(contactosClienteIniciales);
                          setEditContactosClienteAntes(contactosClienteIniciales);
                          setEditContactosAgencia(contactosAgenciaIniciales);
                          setEditContactosAgenciaAntes(contactosAgenciaIniciales);
                          setEditCategoriaCliente(cliente.categoria_cliente || "");
                          setEditIntermediario(cliente.agencia || "");
                          setEditCategoria(cliente.categoria ? String(cliente.categoria) : "");
                          setEditSubcategoria(cliente.subcategoria_id ? String(cliente.subcategoria_id) : "");
                          setEditEjecutivosAsignados(cliente.ejecutivos_asignados?.map(String) || []);
                          setDropdownCountEjecutivos(
                            (cliente.ejecutivos_asignados?.length || 0) +
                              (cliente.ejecutivos_asignados?.length < 4 ? 1 : 0)
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    )
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {deleteClienteId && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={() => setDeleteClienteId(null)}
          >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-0 pb-2">
                  <h5 className="modal-title fw-bold">Confirmar eliminacion</h5>
                  <button type="button" className="btn-close" onClick={() => setDeleteClienteId(null)} />
                </div>
                <div className="modal-body pt-2">
                  <p className="mb-0 text-muted">
                    Estas seguro de que deseas eliminar este cliente?
                    <br />
                    <span className="text-danger fw-semibold">Esta accion no se puede deshacer.</span>
                  </p>
                </div>
                <div className="modal-footer border-0 pt-2">
                  <button type="button" className="btn btn-secondary" onClick={() => setDeleteClienteId(null)}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmDeleteCliente}>
                    Si, eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
